/**
 * SOLID Review
 * S — Strong. One concern: the table's video chat as data — the media state a tab keeps per room, the
 *     server's signaling calls and the admin reads, and the commands the media decisions take.
 * O — Good. A new media call is one more request/answer pair.
 * L — N/A — no inheritance hierarchy.
 * I — Good. Only the media moves.
 * D — Strong. zod only.
 */
import { z } from 'zod';

/**
 * THE TABLE'S VIDEO CHAT (frontend strict-ports plan, FE5b/FE5c, 2026-10-04). LiveKit is opt-in per table:
 * the player turns it on (📡), gameplay keeps it up, and an idle table turns it off (§23.7) — as does the
 * server's own idle sweep (§21.10). useLiveKitSession kept that in React state beside the SDK room; the
 * policy is the session's and the Domain's now, and the SDK's room, tracks and participants stay the hook's.
 */

/** The media state a tab keeps, for ONE room: another room's reads as fresh (the hook's reset on leaving). */
export const ClientMedia = z.object({
  roomId: z.string().nullable(),
  /** The player turned video chat on, and nothing turned it off since. */
  enabled: z.boolean(),
  /** When it was (re)enabled — the idle clock never runs from before it. */
  enabledAt: z.number().nullable(),
  /** The idle rule (or the server's sweep) turned it off: the banner offers to reconnect. */
  autoDisconnected: z.boolean(),
  idleBannerDismissed: z.boolean(),
  /** Whether this room's audio and video use were reported — once each per room. */
  reportedAudio: z.boolean(),
  reportedVideo: z.boolean(),
}).meta({ id: 'ClientMedia' });

/** No video chat at any table. */
export const NO_MEDIA: z.infer<typeof ClientMedia> = {
  roomId: null, enabled: false, enabledAt: null, autoDisconnected: false, idleBannerDismissed: false, reportedAudio: false, reportedVideo: false,
};

// ── the server's signaling and admin reads ────────────────────────────────────────────────────

/** The room and player a LiveKit token is for — the SDK's, which may be a room just left. */
export const RequestMediaToken = z.object({ roomId: z.string(), playerId: z.string() }).meta({ id: 'RequestMediaToken' });
export const DtoMediaToken = z.object({ token: z.string(), serverUrl: z.string() }).meta({ id: 'DtoMediaToken' });
export const RequestMediaConnection = z.object({ roomId: z.string(), playerId: z.string(), connected: z.boolean() }).meta({ id: 'RequestMediaConnection' });
export const RequestMediaPresence = z.object({ roomId: z.string(), usedVideo: z.boolean(), usedAudio: z.boolean() }).meta({ id: 'RequestMediaPresence' });
export const RequestMediaHealth = z.object({ playerId: z.string() }).meta({ id: 'RequestMediaHealth' });
export const DtoMediaHealth = z.object({
  configured: z.boolean(), activeRooms: z.number().int(), activeParticipants: z.number().int(),
}).meta({ id: 'DtoMediaHealth' });
export const RequestMediaUsage = z.object({ playerId: z.string(), startDate: z.string(), endDate: z.string() }).meta({ id: 'RequestMediaUsage' });
export const DtoMediaUsage = z.object({
  totalMinutes: z.number(),
  entries: z.array(z.object({ date: z.string(), playerId: z.string(), minutes: z.number() })),
}).meta({ id: 'DtoMediaUsage' });

// ── the decisions' commands ───────────────────────────────────────────────────────────────────

/** The browser's clock, for a move that starts the idle clock (no LOCAL clock port yet — FE5c). */
export const CommandMediaClock = z.object({ now: z.number() }).meta({ id: 'CommandMediaClock' });
/** One tick of the idle check: the clock and the configured timeout (an env value, the Framework's). */
export const CommandMediaIdle = z.object({ now: z.number(), idleTimeoutMs: z.number().int().positive() }).meta({ id: 'CommandMediaIdle' });
export const CommandMediaBanner = z.object({ dismissed: z.boolean() }).meta({ id: 'CommandMediaBanner' });
/** The player turned on their microphone or camera in this room. */
export const CommandMediaUsed = z.object({ roomId: z.string(), kind: z.enum(['audio', 'video']) }).meta({ id: 'CommandMediaUsed' });
/** The presence to report, or none: a guest, or already reported for this room. */
export const DtoPresencePlan = z.object({ request: RequestMediaPresence.nullable() }).meta({ id: 'DtoPresencePlan' });

export type IEntityClientMedia = z.infer<typeof ClientMedia>;
export type IPayloadMediaToken = z.infer<typeof RequestMediaToken>;
export type IEntityMediaToken = z.infer<typeof DtoMediaToken>;
export type IPayloadMediaConnection = z.infer<typeof RequestMediaConnection>;
export type IPayloadMediaPresence = z.infer<typeof RequestMediaPresence>;
export type IPayloadMediaHealth = z.infer<typeof RequestMediaHealth>;
export type IEntityMediaHealth = z.infer<typeof DtoMediaHealth>;
export type IPayloadMediaUsage = z.infer<typeof RequestMediaUsage>;
export type IEntityMediaUsage = z.infer<typeof DtoMediaUsage>;
export type ICommandMediaClock = z.infer<typeof CommandMediaClock>;
export type ICommandMediaIdle = z.infer<typeof CommandMediaIdle>;
export type ICommandMediaBanner = z.infer<typeof CommandMediaBanner>;
export type ICommandMediaUsed = z.infer<typeof CommandMediaUsed>;
export type IEntityPresencePlan = z.infer<typeof DtoPresencePlan>;
